import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import {
  MonthlyExtractManifestDto,
  MonthlyExtractRunDto,
  useLazyGetMonthlyExtractObjectUrlQuery,
} from "src/features/things/monthlyExtractApi"
import { fmtBytes } from "./monthlyReportsChrome"

type FileRow = { key: string; filename: string; kind: string; bytes?: number }

/** The run's own recorded keys, sized from the manifest when it is there. LOCKER-500: no zip. */
const filesOf = (run: MonthlyExtractRunDto, manifest: MonthlyExtractManifestDto | null): FileRow[] => {
  const keys = run.s3Keys
  if (!keys) return []
  const size = new Map<string, number>()
  for (const w of manifest?.workbooks ?? []) size.set(w.key, w.bytes)
  const name = (k: string) => k.slice(k.lastIndexOf("/") + 1)
  return [
    ...keys.workbooks.map((k) => ({ key: k, filename: name(k), kind: "workbook", bytes: size.get(k) })),
    { key: keys.manifest, filename: name(keys.manifest), kind: "manifest" },
  ]
}

/**
 * LOCKER-469 DoD2 — the archived files, each downloaded through a five-minute
 * presigned GET the server mints only for a key the run itself recorded.
 */
export const RunFilesPanel: FC<{
  run: MonthlyExtractRunDto
  manifest: MonthlyExtractManifestDto | null
}> = ({ run, manifest }) => {
  const [getUrl] = useLazyGetMonthlyExtractObjectUrlQuery()
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const files = filesOf(run, manifest)

  const download = async (key: string) => {
    setBusyKey(key)
    setFailure(null)
    try {
      const res = await getUrl({ runId: run.id, key }).unwrap()
      // A navigation, not a fetch: the object is served as an attachment and the
      // bucket needs no CORS.
      window.location.assign(res.data.url)
    } catch (e: any) {
      setFailure(e?.data?.message ?? "could not get a download link")
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-ui="UI-SL2W-279">
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Files</Typography>
      {!files.length && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Nothing archived — the run stopped before its files were written.
        </Typography>
      )}
      {!!failure && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {failure}
        </Alert>
      )}
      <Table size="small">
        <TableBody>
          {files.map((f) => (
            <TableRow key={f.key}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{f.filename}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{f.kind}</TableCell>
              <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                {f.bytes !== undefined ? fmtBytes(f.bytes) : ""}
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  disabled={busyKey !== null}
                  onClick={() => void download(f.key)}
                  aria-label={`download ${f.filename}`}
                >
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}
